import { useCurrentCompany, useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { useBizyOnboarding } from '../../queries/bizy-onboarding';
import { useUserContentDismissal } from '../profile/user-content-dismissal';
import { MEET_BIZY_PROMO_NAME, MEET_BIZY_PROMO_NAMESPACE } from './meet-bizy-promo-constants';

/**
 * Whether the Bizy intro takeover is still waiting to be shown, so the Bizy
 * side panel doesn't auto-open behind it. The takeover is the onboarding flow
 * (`<BizyOnboarding>` — additionally gated on onboarding never having started).
 *
 * `isResolved` is reported apart from `isPending` because a caller that latches
 * a once-per-load decision has to tell "no answer yet" — where every fresh load
 * starts, and where returning `isPending: true` would be a guess — from
 * "answered: the takeover owns this screen".
 */
const useMeetBizyPromoTakeover = (): { isResolved: boolean; isPending: boolean } => {
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const company = useCurrentCompany();
	const isEligible = isBizyEnabledForUser && !company?.isTrial;
	const { loading, data } = useUserContentDismissal({
		namespace: MEET_BIZY_PROMO_NAMESPACE,
		name: MEET_BIZY_PROMO_NAME,
		enabled: isEligible,
	});
	const { data: onboarding, isLoading: isOnboardingLoading } = useBizyOnboarding(isEligible);

	if (!isEligible) return { isResolved: true, isPending: false };
	// Wait for the dismissal record first — once we know the user dismissed,
	// the onboarding query result is irrelevant (the takeover won't show either way).
	if (loading) return { isResolved: false, isPending: false };
	if (data?.dismissed_at) return { isResolved: true, isPending: false };
	// Wait for the stage to decide, but if the query failed (data undefined
	// after loading) don't hold the panel hostage — the takeover can't open
	// either, so let the side panel auto-open.
	if (isOnboardingLoading) return { isResolved: false, isPending: false };
	// Onboarding already started (or finished, or the query failed) — the
	// takeover no longer auto-opens. Note this flips mid-flow: advancing a card
	// writes `onboardingStage`, so "pending" goes false while the modal is still
	// on screen.
	return { isResolved: true, isPending: onboarding !== undefined && onboarding.onboardingStage === 0 };
};

/**
 * True while the takeover may still appear, including before we know. Use this
 * to hold something back; use {@link useIsMeetBizyPromoResolved} alongside it if
 * you also need to know whether the answer has arrived.
 */
export const useHasPendingMeetBizyPromo = (): boolean => {
	const { isResolved, isPending } = useMeetBizyPromoTakeover();
	return !isResolved || isPending;
};

/** Whether the takeover's queries have answered, either way. */
export const useIsMeetBizyPromoResolved = (): boolean => useMeetBizyPromoTakeover().isResolved;
